// @ts-nocheck
// src/pages/FamilyApplicationsAdmin.jsx
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "../components/Spinner";
import ErrorPage from "../components/ErrorPage";
import Modal from "../components/Modal";
import FamilyApplicationTable from "../components/FamilyApplicationTable";
import { ParentMergeWizardModal } from "../features/parentMerge";
import { FaFileAlt, FaSearch, FaSortAmountDown, FaSyncAlt, FaTimes } from "react-icons/fa";
import http from "../api/apiClient";
import { useCan } from "../authz/useCan";
import { AuthContext } from "../AuthProvider";
import {
  PlacementHero,
  PlacementPageShell,
  PlacementSection,
} from "./planning/components/PlacementLds";

const FamilyApplicationsAdmin: React.FC = () => {
  const { t } = useTranslation();
  const { userInfo } = useContext(AuthContext);

  // 🔐 backend-gedreven permissies
  const canCrud = useCan("school.applications", "crud");
  const canManage = useCan("school.applications", "manage");
  const canModerate = canCrud || canManage;
  const canAct = canCrud || canManage;

  const [applications, setApplications] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [teachersError, setTeachersError] = useState(null);
  const [activeTab, setActiveTab] = useState("OPEN");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("NEWEST");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState(null);

  const [conflictData, setConflictData] = useState(null);
  const [applicationToApprove, setApplicationToApprove] = useState(null);
  const [pendingApprovalPlacements, setPendingApprovalPlacements] = useState({});
  const [drawerCloseSignal, setDrawerCloseSignal] = useState(0);

  const fetchApplications = async () => {
    if (!canModerate) {
      setLoading(false);
      return;
    }
    setRefreshing(true);
    setError(null);
    try {
      const { data } = await http.get(`/applications`);
      // ondersteunt zowel array als paged payload
      const items = Array.isArray(data)
        ? data
        : (data?.content ?? data?.items ?? data?.data ?? []);
      setApplications(items);
    } catch (e) {
      if (e?.response?.status === 403) {
        setError(
          t("applications.forbidden", "Je hebt geen toegang tot deze pagina."),
        );
      } else {
        setError(
          t("applications.error_loading", "Kon aanmeldingen niet laden."),
        );
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canModerate]);

  const fetchTeachers = useCallback(async () => {
    if (!canModerate) return;
    setTeachersLoading(true);
    setTeachersError(null);
    try {
      const { data } = await http.get("/teachers");
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.content)
          ? data.content
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.data)
              ? data.data
              : [];
      setTeachers(list);
    } catch (err) {
      setTeachers([]);
      setTeachersError(err);
    } finally {
      setTeachersLoading(false);
    }
  }, [canModerate]);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  // ---- acties (alleen met crud) ----
  const handleDecision = async (id, action, childPlacements = {}) => {
    if (!canAct) return;
    try {
      await http.put(`/applications/${id}/${action}`,
        action === "approve" ? { childPlacements } : {});
      fetchApplications();
    } catch (err) {
      // 409 met conflicts → toon merge wizard
      if (err?.response?.status === 409 && err.response.data?.conflict) {
        const nextConflictData = err.response.data;
        setDrawerCloseSignal((signal) => signal + 1);
        window.requestAnimationFrame(() => {
          setApplicationToApprove(id);
          setPendingApprovalPlacements(childPlacements);
          setConflictData(nextConflictData);
        });
      } else {
        console.error("Failed to execute application action:", err);
        setConflictData(null);
        setApplicationToApprove(null);
        const message =
          err?.response?.data?.message ||
          err?.message ||
          t("applications.error_action", "Kon de actie niet uitvoeren.");
        setError(message);
      }
    }
  };

  const handleReject = (id) => handleDecision(id, "reject");

  const handleWorkflowStep = async (id, step, completed, options = {}) => {
    if (!canAct) return;
    try {
      const params = new URLSearchParams({ completed: String(completed) });
      if (options.scheduledAt) params.set("scheduledAt", options.scheduledAt);
      await http.patch(`/applications/${id}/workflow/${step}?${params.toString()}`);
      await fetchApplications();
      return true;
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.detail ||
          t("applications.workflow.update_failed", {
            defaultValue: "The onboarding step could not be updated.",
          }),
      );
      return false;
    }
  };

  const handleRestore = async (id) => {
    if (!canAct) return;
    try {
      await http.put(`/applications/${id}/restore`, {});
      fetchApplications();
    } catch (err) {
      throw err;
    }
  };

  const confirmDelete = (id) => {
    if (!canAct) return;
    setApplicationToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!canAct || !applicationToDelete) return;
    try {
      // backend hanteert soft-delete via PUT op /delete
      await http.put(`/applications/${applicationToDelete}/delete`, {});
      fetchApplications();
    } finally {
      setShowDeleteModal(false);
      setApplicationToDelete(null);
    }
  };

  // Merge flow vanuit conflict wizard
  const resolveConflict = async (strategy, merged = {}) => {
    if (!canAct || !applicationToApprove) return;
    try {
      await http.put(`/applications/${applicationToApprove}/merge`, {
        resolutionStrategy: strategy,
        mergedParent1: merged?.parent1 || {},
        mergedParent2: merged?.parent2 || {},
        mergedChildren: merged?.children || [],
      });
      await http.put(`/applications/${applicationToApprove}/approve`, {
        childPlacements: pendingApprovalPlacements,
      });
      setConflictData(null);
      setApplicationToApprove(null);
      setPendingApprovalPlacements({});
      fetchApplications();
    } catch (error) {
      throw error;
    }
  };

  const filtered = useMemo(() => {
    const normalizeSearchValue = (value) => String(value || "")
      .toLocaleLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const query = normalizeSearchValue(searchQuery.trim());
    const searchablePersonValues = (person) => {
      if (!person) return [];
      const firstName = person.firstName || person.first_name || "";
      const lastName = person.lastName || person.last_name || "";
      return [
        firstName,
        lastName,
        `${firstName} ${lastName}`,
        person.displayName,
        person.display_name,
        person.name,
        person.email,
      ];
    };

    const getSubmittedAt = (application) => {
      const value = application?.createdAt || application?.created_at || application?.submittedAt;
      if (!value) return null;
      const timestamp = new Date(value).getTime();
      return Number.isNaN(timestamp) ? null : timestamp;
    };
    const getApplicantName = (application) => {
      const parent = application?.parent1 || application?.parent2 || {};
      return normalizeSearchValue(
        [parent.firstName || parent.first_name, parent.lastName || parent.last_name]
          .filter(Boolean)
          .join(" "),
      );
    };

    const visibleApplications = applications.filter((a) => {
      const normalized = (a?.status || "").toUpperCase();
      const matchesStatus = activeTab === "OPEN"
        ? normalized === "PENDING" || normalized === "NEW"
        : normalized === activeTab;
      if (!matchesStatus || !query) return matchesStatus;

      return [a?.parent1, a?.parent2, ...(a?.children || [])]
        .flatMap(searchablePersonValues)
        .filter(Boolean)
        .some((value) => normalizeSearchValue(value).includes(query));
    });

    return visibleApplications.sort((left, right) => {
      if (sortOrder === "NAME_ASC" || sortOrder === "NAME_DESC") {
        const direction = sortOrder === "NAME_ASC" ? 1 : -1;
        return getApplicantName(left).localeCompare(getApplicantName(right), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * direction;
      }

      const leftTime = getSubmittedAt(left);
      const rightTime = getSubmittedAt(right);
      if (leftTime === null && rightTime === null) return 0;
      if (leftTime === null) return 1;
      if (rightTime === null) return -1;
      return sortOrder === "OLDEST" ? leftTime - rightTime : rightTime - leftTime;
    });
  }, [applications, activeTab, searchQuery, sortOrder]);

  const teacherLookup = useMemo(() => {
    const map = new Map();
    (Array.isArray(teachers) ? teachers : []).forEach((teacher) => {
      const key =
        teacher.id ??
        teacher.teacherId ??
        teacher.teacher_id ??
        teacher.uuid ??
        teacher.teacherUUID ??
        teacher.teacherUuid;
      if (!key) return;
      const label =
        [teacher.firstName, teacher.lastName].filter(Boolean).join(" ") ||
        [teacher.first_name, teacher.last_name].filter(Boolean).join(" ") ||
        teacher.displayName ||
        teacher.display_name ||
        teacher.email ||
        String(key);
      map.set(String(key), label);
    });
    return map;
  }, [teachers]);

  const currentTeacherId = useMemo(() => {
    const subject = String(userInfo?.sub || "");
    if (!subject) return null;
    const matches = (Array.isArray(teachers) ? teachers : []).filter(
      (item) => String(item.keycloakUserId || "") === subject,
    );
    // Alleen een ondubbelzinnige, harde Keycloak-koppeling mag “mijzelf” bepalen.
    return matches.length === 1 ? matches[0]?.id ?? null : null;
  }, [teachers, userInfo?.sub]);

  const handleAssignTeacher = useCallback(
    async (applicationId, teacherId) => {
      if (!canAct || !applicationId) return;
      try {
        const url = teacherId
          ? `/applications/${applicationId}/assign-teacher?teacherId=${encodeURIComponent(
              teacherId,
            )}`
          : `/applications/${applicationId}/assign-teacher`;
        await http.patch(url);
        await fetchApplications();
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          t("applications.assign_teacher_failed", "Kon docent niet koppelen.");
        setError(message);
      }
    },
    [canAct, fetchApplications, t],
  );

  if (loading) return <Spinner />;

  if (!canModerate) {
    return (
      <ErrorPage
        status={403}
        message={t(
          "applications.forbidden",
          "Je hebt geen toegang tot deze pagina.",
        )}
        onRetry={fetchApplications}
      />
    );
  }

  if (error) {
    return (
      <ErrorPage
        message={error}
        status={error.includes("toegang") ? 403 : 500}
        onRetry={fetchApplications}
      />
    );
  }

  return (
    <>
      <style>{`
        @keyframes lds-page-enter {
          from {
            opacity: 0;
            transform: translateY(14px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes lds-content-enter {
          from {
            opacity: 0;
            transform: translateY(18px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .lds-page-enter {
          animation: lds-page-enter 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .lds-page-enter > * {
          animation: lds-content-enter 480ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .lds-page-enter > *:nth-child(2) {
          animation-delay: 45ms;
        }

        .lds-page-enter > *:nth-child(3) {
          animation-delay: 90ms;
        }

        .lds-page-enter > *:nth-child(4) {
          animation-delay: 135ms;
        }

        @media (prefers-reduced-motion: reduce) {
          .lds-page-enter,
          .lds-page-enter > * {
            animation: none;
          }
        }
      `}</style>
      <div className="lds-page-enter">
    <PlacementPageShell>
      <PlacementHero
        eyebrow={t("placement.overview.title", { defaultValue: "Placement" })}
        title={t("applications.title", { defaultValue: "Applications" })}
        description={t("applications.description", {
          defaultValue: "Review, assign and process new family applications.",
        })}
        icon={<FaFileAlt aria-hidden="true" />}
        illustration={<FaFileAlt aria-hidden="true" />}
        badges={
          <span className="rounded-full border border-indigo-200 bg-white/80 px-3 py-1 text-xs font-semibold text-indigo-700 dark:border-indigo-500/40 dark:bg-slate-900/70 dark:text-indigo-200">
            {filtered.length}{" "}
            {t("applications.visible", { defaultValue: "visible" })}
          </span>
        }
      />
      {teachersError && (
        <div className="mb-4 rounded-2xl border border-amber-300/70 bg-amber-50/80 p-3 text-sm text-amber-900 dark:border-amber-500/50 dark:bg-amber-900/40 dark:text-amber-100">
          {t(
            "applications.error_loading_teachers",
            "Kon docentenlijst niet laden.",
          )}
        </div>
      )}
      <PlacementSection className="flex flex-wrap items-center gap-3 p-4">
        <div className="inline-flex rounded-full bg-gray-100 p-1 dark:bg-gray-800">
          {[
            { key: "OPEN", label: t("applications.tabs.open", "Openstaand") },
            {
              key: "APPROVED",
              label: t("applications.tabs.approved", "Goedgekeurd"),
            },
            {
              key: "REJECTED",
              label: t("applications.tabs.rejected", "Afgewezen"),
            },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                activeTab === key
                  ? "bg-white text-indigo-600 shadow-lg ring-2 ring-indigo-200/70 dark:bg-gray-900 dark:text-indigo-200 dark:ring-indigo-500/40"
                  : "text-gray-500 hover:text-indigo-600 hover:ring-2 hover:ring-indigo-200/50 dark:text-gray-300 dark:hover:ring-indigo-500/30"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <label className="relative min-w-[16rem] flex-1 sm:max-w-md">
          <span className="sr-only">
            {t("applications.search_label", { defaultValue: "Search applications" })}
          </span>
          <FaSearch
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t("applications.search_placeholder", {
              defaultValue: "Search parents or children",
            })}
            className="h-11 w-full rounded-full border border-slate-200 bg-white/90 pl-11 pr-11 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-900/70 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-900/50"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              aria-label={t("applications.clear_search", { defaultValue: "Clear search" })}
              className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
            >
              <FaTimes aria-hidden="true" />
            </button>
          )}
        </label>

        <label className="relative min-w-[12rem]">
          <span className="sr-only">
            {t("applications.sort_label", { defaultValue: "Sort applications" })}
          </span>
          <FaSortAmountDown
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <select
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
            aria-label={t("applications.sort_label", { defaultValue: "Sort applications" })}
            className="h-11 w-full appearance-none rounded-full border border-slate-200 bg-white/90 pl-11 pr-9 text-sm font-semibold text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-900/70 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900/50"
          >
            <option value="NEWEST">{t("applications.sort.newest", { defaultValue: "Newest first" })}</option>
            <option value="OLDEST">{t("applications.sort.oldest", { defaultValue: "Oldest first" })}</option>
            <option value="NAME_ASC">{t("applications.sort.name_asc", { defaultValue: "Name A–Z" })}</option>
            <option value="NAME_DESC">{t("applications.sort.name_desc", { defaultValue: "Name Z–A" })}</option>
          </select>
        </label>

        <button
          onClick={fetchApplications}
          className="lumen-glow-button text-sm disabled:opacity-60"
          disabled={refreshing}
        >
          <FaSyncAlt className={refreshing ? "mr-2 animate-spin" : "mr-2"} />
          {t("common.refresh", "Ververs")}
        </button>
      </PlacementSection>

      <PlacementSection className="overflow-hidden p-4">
        <FamilyApplicationTable
          applications={filtered}
          drawerCloseSignal={drawerCloseSignal}
          // moderatie-acties tonen als toegestaan + passend bij tab
          onApprove={
            canAct && activeTab === "OPEN"
              ? (id, childPlacements) => handleDecision(id, "approve", childPlacements)
              : undefined
          }
          onReject={
            canAct && activeTab === "OPEN"
              ? (id) => handleReject(id)
              : undefined
          }
          onDelete={
            canAct && activeTab === "REJECTED"
              ? (id) => confirmDelete(id)
              : undefined
          }
          onRestore={
            canAct && activeTab === "REJECTED"
              ? (id) => handleRestore(id)
              : undefined
          }
          onAssignTeacher={canAct ? handleAssignTeacher : undefined}
          onWorkflowStep={canAct ? handleWorkflowStep : undefined}
          teachers={teachers}
          teachersLoading={teachersLoading}
          teacherLookup={teacherLookup}
          currentTeacherId={currentTeacherId}
          emptyMessage={searchQuery
            ? t("applications.no_search_results", { defaultValue: "No matching applications found." })
            : undefined}
          showStatus
        />
      </PlacementSection>

      <Modal
        show={showDeleteModal}
        title={t("common.modal.are_you_sure", "Weet je het zeker?")}
        message={t(
          "common.modal.action_cannot_be_undone",
          "Deze actie kan niet ongedaan worden gemaakt.",
        )}
        isConfirm
        type="confirm"
        onConfirm={handleDelete}
        onClose={() => setShowDeleteModal(false)}
      />

      <ParentMergeWizardModal
        show={!!conflictData}
        existingParent1={conflictData?.existingParent1}
        newParent1={conflictData?.newParent1}
        existingParent2={conflictData?.existingParent2}
        newParent2={conflictData?.newParent2}
        existingChildren={conflictData?.existingChildren || []}
        newChildren={conflictData?.newChildren || []}
        onSubmit={({ parent1, parent2, children }) =>
          resolveConflict("MERGE", { parent1, parent2, children })
        }
        onClose={() => {
          setConflictData(null);
          setApplicationToApprove(null);
        }}
      />
    </PlacementPageShell>

      </div>
    </>
  );
};

export default FamilyApplicationsAdmin;
